import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import './i18n'
import App from './App.tsx'

// Stale-deploy safety net: when a tab outlives a deploy, the cached
// index.html references /assets/*.js files the server no longer has.
// That surfaces as either (a) a resource error on <script type="module">
// or (b) a dynamic-import rejection inside React.lazy. In both cases the
// right move is to reload once — a fresh index.html points at the new
// hashes. The SESSION_FLAG guard makes sure we never loop if the reload
// itself hits the same error.
const CHUNK_RELOAD_FLAG = '__chunk_load_reloaded__';

function reloadForStaleDeploy(reason: string) {
  if (sessionStorage.getItem(CHUNK_RELOAD_FLAG)) return;
  sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
  console.warn('[build-version] forcing reload:', reason);
  // Tear down SW + caches, matching reloadToLatest in lib/build-version.ts.
  // Inlined here to keep main.tsx import-light on the critical path.
  (async () => {
    if ('serviceWorker' in navigator) {
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      } catch { /* swallow */ }
    }
    if ('caches' in window) {
      try {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      } catch { /* swallow */ }
    }
    window.location.reload();
  })();
}

// Resource-load failures don't bubble, so we listen in the capture phase.
window.addEventListener('error', (e) => {
  const target = e.target as HTMLElement | null;
  if (!target) return;
  const isAssetScript =
    target instanceof HTMLScriptElement &&
    (target.src || '').includes('/assets/');
  const isAssetStyle =
    target instanceof HTMLLinkElement &&
    target.rel === 'stylesheet' &&
    (target.href || '').includes('/assets/');
  if (isAssetScript || isAssetStyle) {
    reloadForStaleDeploy(`resource load failed: ${(target as any).src || (target as any).href}`);
  }
}, true);

// React.lazy + dynamic import() failures surface as unhandled rejections.
window.addEventListener('unhandledrejection', (e) => {
  const msg = String(e.reason?.message || e.reason || '');
  if (/Failed to fetch dynamically imported module|Loading chunk|Importing a module script failed/i.test(msg)) {
    reloadForStaleDeploy(`dynamic import failed: ${msg}`);
  }
});

// Clear the reload flag once the app successfully renders — this lets the
// next deploy take effect normally instead of being blocked by a stale
// flag from a previous session's recovery.
requestAnimationFrame(() => {
  try { sessionStorage.removeItem(CHUNK_RELOAD_FLAG); } catch { /* swallow */ }
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)

// Register service worker for PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // SW registration failed
    });
  });
}
